import {
  DEFAULT_SERIES,
  DEFAULT_SESSION,
  type Match,
  type MatchOptions,
  type MatchStatus,
  matchStatus,
  newMatch,
  parseMatch,
  recordGame,
  serializeMatch,
} from './match.js';

/**
 * A weekend of play: games add up to a SESSION (first to 10 game wins, within 15 games), and sessions
 * add up to a SERIES (first side to win 3 sessions, within 5). Both levels are the same `Match`; this
 * only wires the second to the first. A session that ends level is drawn and gives each leader a
 * session win, the same shared-win rule as a game.
 */
export interface CompletedSession<Key extends string> {
  readonly winners: readonly Key[];
  readonly wins: Readonly<Record<Key, number>>;
  readonly games: number;
}

export interface Tournament<Key extends string> {
  readonly keys: readonly Key[];
  readonly session: Match<Key>;
  readonly series: Match<Key>;
  readonly history: readonly CompletedSession<Key>[];
}

export interface TournamentOptions {
  readonly session?: MatchOptions;
  readonly series?: MatchOptions;
}

export function newTournament<Key extends string>(keys: readonly Key[], opts: TournamentOptions = {}): Tournament<Key> {
  return {
    keys,
    session: newMatch(keys, opts.session ?? DEFAULT_SESSION),
    series: newMatch(keys, opts.series ?? DEFAULT_SERIES),
    history: [],
  };
}

/** The state of the whole tournament: the series decides it. */
export const tournamentStatus = <Key extends string>(t: Tournament<Key>): MatchStatus<Key> => matchStatus(t.series);

/**
 * Record one finished game (several winners = a shared win). When it ends the session, the session's
 * winner (or its leaders, if drawn) is recorded in the series and a fresh session starts, unless the
 * series is over.
 */
export function recordTournamentGame<Key extends string>(t: Tournament<Key>, winners: readonly Key[]): Tournament<Key> {
  if (tournamentStatus(t).state !== 'playing') throw new Error('the tournament is already over');
  const session = recordGame(t.session, winners);
  const status = matchStatus(session);
  if (status.state === 'playing') return { ...t, session };

  const sessionWinners = status.state === 'won' ? [status.winner] : status.leaders;
  const series = recordGame(t.series, sessionWinners);
  const history = [...t.history, { winners: sessionWinners, wins: session.wins, games: session.games }];
  return { ...t, session: newMatch(t.keys, t.session.options), series, history };
}

export const serializeTournament = <Key extends string>(t: Tournament<Key>): string =>
  JSON.stringify({ v: 1, keys: t.keys, session: serializeMatch(t.session), series: serializeMatch(t.series), history: t.history });

/** Read a saved tournament; anything absent, malformed or inconsistent gives null (start a fresh one). */
export function parseTournament<Key extends string>(raw: string | null | undefined, keys: readonly Key[]): Tournament<Key> | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Record<string, unknown>;
    if (d.v !== 1 || typeof d.session !== 'string' || typeof d.series !== 'string' || !Array.isArray(d.history)) return null;
    const session = parseMatch(d.session, keys);
    const series = parseMatch(d.series, keys);
    if (!session || !series) return null;
    if (matchStatus(session).state !== 'playing') return null; // a finished session is always rolled into the series
    if (d.history.length !== series.games) return null;
    const history: CompletedSession<Key>[] = [];
    for (const h of d.history as Record<string, unknown>[]) {
      const winners = h.winners as unknown;
      const games = h.games;
      if (!Array.isArray(winners) || winners.length === 0 || !winners.every((w) => keys.includes(w as Key))) return null;
      if (typeof games !== 'number' || !Number.isInteger(games) || games < 1) return null;
      const wins = h.wins as Record<string, unknown> | undefined;
      if (!wins || !keys.every((k) => typeof wins[k] === 'number')) return null;
      history.push({ winners: winners as Key[], wins: wins as Record<Key, number>, games });
    }
    return { keys, session, series, history };
  } catch {
    return null;
  }
}
